import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import { SpinViewer } from "hello-mono-sdk/packages/spin";
import { getLowQualityImg } from "../../utils/image";
import { HelloProduct } from "../../types/backend";
import { getSpinData } from "../../utils/contentType";
import "./SpinViewer.scss";

import ZoomBar from "./ZoomBar";
import ZoomImageManager from "./ZoomManager";

const zoomManager = new ZoomImageManager();

type IProps = {
  product: HelloProduct;
  hideArButton: () => void;
  showArButton: () => void;
};

const SpinViewerReact: React.FC<IProps> = ({
  product,
  hideArButton,
  showArButton,
}) => {
  const viewerRef: any = useRef(null);
  const firstLoad = useRef<boolean>(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [loadingProgress, setLoadingProgress] = useState(0);
  zoomManager.setOnChangeReact(setZoomLevel);

  const image = useMemo(() => {
    return getSpinData(product);
  }, [product]);

  useEffect(() => {
    setLoadingProgress(0);
    setZoomLevel(1);
    if (viewerRef.current && viewerRef.current.initialized) {
      viewerRef.current.destroy();
      viewerRef.current.init();
    } else if (viewerRef.current) {
      //if Spinner360Image class constructor has been called but not been initialized
      viewerRef.current.init();
    }

    return () => {
      if (viewerRef.current) {
        viewerRef.current.destroy();
      }
    };
  }, [image?.image_folder_url]);

  const onDivMounted = useCallback((divRef: HTMLDivElement) => {
    if (!viewerRef.current) {
      viewerRef.current = new SpinViewer(
        divRef,
        undefined,
        undefined,
        "contain",
        (progress: number) => {
          console.log("on progress", progress);
          setLoadingProgress(progress);
        },
        (newZoom: number) => {
          setZoomLevel(newZoom);
        },
        undefined,
        undefined,
        zoomManager,
        {
          position: {
            right: isMobile ? "10px" : "26px",
            bottom: isMobile ? "140px" : "40px",
          },
        }
      );
      viewerRef.current.init();
      firstLoad.current = true;
    }
  }, []);

  const imageFolderUrl = useMemo(() => {
    return image?.image_folder_url
      ? getLowQualityImg(image.image_folder_url, 1920)
      : "";
  }, [image?.image_folder_url]);

  if (zoomLevel > 1) {
    hideArButton();
  } else {
    showArButton();
  }

  return (
    <div className="spin-container">
      {loadingProgress !== 100 && (
        <div className="loader-container">
          <div className="loader-wrapper">
            <div className="loader-outline">
              <div
                className="loader-progress"
                style={{ width: `${loadingProgress}%` }}
              />

              <span className="loader-text">{loadingProgress}%</span>
            </div>
          </div>
        </div>
      )}

      {loadingProgress === 100 && (
        <ZoomBar zoomLevel={zoomLevel} zoomManager={zoomManager} />
      )}

      <div
        className="spin-image-container"
        style={{ visibility: loadingProgress !== 100 ? "hidden" : "visible" }}
      >
        {imageFolderUrl.length > 0 ? (
          <div
            ref={onDivMounted}
            data-folder={imageFolderUrl}
            data-amount={image?.image_count ?? 0}
            data-start-at={image?.start_at}
            data-end-at={image?.end_at}
          ></div>
        ) : (
          <div>
            <h1>No Image Found</h1>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpinViewerReact;
